import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Edit, Trash2, Save, Image, MessageSquare, 
  HelpCircle, Eye, EyeOff, GripVertical, Star, Globe, RotateCcw,
  ChevronLeft, ChevronRight, FileText, Shield
} from 'lucide-react';
import ImageUploadField from '../../components/common/ImageUploadField';
import RichTextEditor from '../../components/common/RichTextEditor';
import { 
  CMSTestimonialsTab, 
  CMSFAQsTab, 
  CMSLegalTab,
  CMSLandingTab,
  TestimonialDialog,
  FAQDialog,
  RestoreDefaultsDialog,
  FAQ_CATEGORIES
} from '../../components/admin/cms';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function AdminCMS() {
  const navigate = useNavigate();
  const [cmsData, setCmsData] = useState({ content: [], banners: [], testimonials: [], faqs: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [activeDialog, setActiveDialog] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [restoreOptions, setRestoreOptions] = useState({
    restore_faqs: true,
    restore_testimonials: true,
    restore_site_identity: false,
    restore_landing: false,
    clear_existing: false
  });
  
  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };
  
  // For backward compatibility with existing code
  const headers = getAuthHeaders();
  
  // Landing page content
  const [heroForm, setHeroForm] = useState({
    title: "Your Home's Hero is Just a Tap Away",
    subtitle: 'Connect with verified, skilled service providers for all your home repair and maintenance needs.',
    cta_text: 'Book a Hero',
    cta_link: '/register',
    background_image: ''
  });
  
  const [footerForm, setFooterForm] = useState({
    company_name: 'HandyBayani',
    tagline: 'Your trusted home services partner',
    email: 'support@handybayani.com',
    phone: '+63 (2) 8123-4567',
    address: 'Manila, Philippines',
    social_links: { facebook: '#', twitter: '#', instagram: '#' },
    copyright_text: '© 2026 HandyBayani. All rights reserved.'
  });
  
  const [siteIdentity, setSiteIdentity] = useState({
    site_title: 'HandyBayani',
    site_tagline: 'Home Services Marketplace',
    site_logo: '',
    favicon: '',
    og_image: '',  // Social media sharing image
    og_description: ''  // Social media description
  });
  
  // Legal pages state
  const [termsContent, setTermsContent] = useState('');
  const [privacyContent, setPrivacyContent] = useState('');
  const [termsLastUpdated, setTermsLastUpdated] = useState('');
  const [privacyLastUpdated, setPrivacyLastUpdated] = useState('');

  // Form states with user-friendly defaults
  const [testimonialForm, setTestimonialForm] = useState({ 
    name: '', location: '', rating: 5, text: '', avatar_url: '', is_active: true 
  });
  const [faqForm, setFaqForm] = useState({ 
    question: '', answer: '', category: 'general', order: 0, is_active: true 
  });

  const fetchData = async () => {
    try {
      const [cmsRes, landingRes, termsRes, privacyRes] = await Promise.all([
        axios.get(`${API}/admin/cms/all`, { headers: getAuthHeaders() }),
        axios.get(`${API}/cms/landing`),
        axios.get(`${API}/cms/legal/terms`),
        axios.get(`${API}/cms/legal/privacy`)
      ]);
      setCmsData(cmsRes.data);
      
      // Load landing page content
      if (landingRes.data.hero) {
        setHeroForm(prev => ({ ...prev, ...landingRes.data.hero }));
      }
      if (landingRes.data.footer) {
        setFooterForm(prev => ({ ...prev, ...landingRes.data.footer }));
      }
      if (landingRes.data.site_identity) {
        setSiteIdentity(prev => ({ ...prev, ...landingRes.data.site_identity }));
      }
      
      // Load legal content
      if (termsRes.data.content) {
        setTermsContent(termsRes.data.content);
      }
      if (termsRes.data.last_updated) {
        setTermsLastUpdated(termsRes.data.last_updated);
      }
      if (privacyRes.data.content) {
        setPrivacyContent(privacyRes.data.content);
      }
      if (privacyRes.data.last_updated) {
        setPrivacyLastUpdated(privacyRes.data.last_updated);
      }
    } catch (error) {
      console.error('Failed to fetch CMS data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Restore defaults handler
  const handleRestoreDefaults = async () => {
    setIsRestoring(true);
    try {
      const params = new URLSearchParams(restoreOptions).toString();
      const response = await axios.post(`${API}/admin/cms/restore-defaults?${params}`, {}, { headers: getAuthHeaders() });
      const results = response.data.results;
      
      let message = 'Restored: ';
      const parts = [];
      if (results.faqs_restored > 0) parts.push(`${results.faqs_restored} FAQs`);
      if (results.testimonials_restored > 0) parts.push(`${results.testimonials_restored} testimonials`);
      if (results.site_identity_reset) parts.push('site identity');
      if (results.landing_restored) parts.push('landing page');
      
      message += parts.length > 0 ? parts.join(', ') : 'No new items (already exist)';
      
      toast.success(message);
      setShowRestoreDialog(false);
      fetchData();
    } catch (error) {
      console.error('Restore defaults error:', error);
      toast.error('Failed to restore defaults');
    } finally {
      setIsRestoring(false);
    }
  };

  // Testimonial handlers
  const handleSaveTestimonial = async () => {
    if (!testimonialForm.name || !testimonialForm.text) {
      toast.error('Please fill in name and testimonial text');
      return;
    }
    setIsSaving(true);
    try {
      if (editItem) {
        await axios.put(`${API}/admin/cms/testimonials/${editItem.id}`, testimonialForm, { headers: getAuthHeaders() });
        toast.success('Testimonial updated!');
      } else {
        await axios.post(`${API}/admin/cms/testimonials`, testimonialForm, { headers: getAuthHeaders() });
        toast.success('Testimonial created!');
      }
      closeDialog();
      fetchData();
    } catch (error) {
      console.error('Save testimonial error:', error);
      toast.error('Failed to save testimonial');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTestimonial = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await axios.delete(`${API}/admin/cms/testimonials/${id}`, { headers: getAuthHeaders() });
      toast.success('Testimonial deleted');
      fetchData();
    } catch (error) {
      console.error('Delete testimonial error:', error);
      toast.error('Failed to delete');
    }
  };

  // FAQ handlers
  const handleSaveFaq = async () => {
    if (!faqForm.question || !faqForm.answer) {
      toast.error('Please fill in the question and answer');
      return;
    }
    setIsSaving(true);
    try {
      if (editItem) {
        await axios.put(`${API}/admin/cms/faq/${editItem.id}`, faqForm, { headers: getAuthHeaders() });
        toast.success('FAQ updated!');
      } else {
        await axios.post(`${API}/admin/cms/faq`, faqForm, { headers: getAuthHeaders() });
        toast.success('FAQ created!');
      }
      closeDialog();
      fetchData();
    } catch (error) {
      console.error('Save FAQ error:', error);
      toast.error('Failed to save FAQ');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteFaq = async (id) => {
    if (!window.confirm('Delete this FAQ?')) return;
    try {
      await axios.delete(`${API}/admin/cms/faq/${id}`, { headers: getAuthHeaders() });
      toast.success('FAQ deleted');
      fetchData();
    } catch (error) {
      console.error('Delete FAQ error:', error);
      toast.error('Failed to delete');
    }
  };

  // Landing Page Content handlers
  const handleSaveHero = async () => {
    setIsSaving(true);
    try {
      await axios.post(`${API}/admin/cms/landing/hero`, heroForm, { headers: getAuthHeaders() });
      toast.success('Hero section updated! Changes are now live on the landing page.');
      fetchData();
    } catch (error) {
      console.error('Save hero error:', error);
      toast.error('Failed to save hero section');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveFooter = async () => {
    setIsSaving(true);
    try {
      await axios.post(`${API}/admin/cms/landing/footer`, footerForm, { headers: getAuthHeaders() });
      toast.success('Footer updated! Changes are now live on the landing page.');
      fetchData();
    } catch (error) {
      console.error('Save footer error:', error);
      toast.error('Failed to save footer');
    } finally {
      setIsSaving(false);
    }
  };

  const closeDialog = () => {
    setActiveDialog(null);
    setEditItem(null);
    setTestimonialForm({ name: '', location: '', rating: 5, text: '', avatar_url: '', is_active: true });
    setFaqForm({ question: '', answer: '', category: 'general', order: 0, is_active: true });
  };

  const handleSaveSiteIdentity = async () => {
    setIsSaving(true);
    try {
      await axios.post(`${API}/admin/cms/landing/site-identity`, siteIdentity, { headers: getAuthHeaders() });
      document.title = siteIdentity.site_tagline 
        ? `${siteIdentity.site_title} - ${siteIdentity.site_tagline}` 
        : siteIdentity.site_title;
      // Update favicon if set
      if (siteIdentity.favicon) {
        const faviconUrl = siteIdentity.favicon.startsWith('http') ? siteIdentity.favicon : `${BACKEND_URL}${siteIdentity.favicon}`;
        let link = document.querySelector("link[rel*='icon']");
        if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
        link.href = faviconUrl;
      }
      toast.success('Site identity saved!');
    } catch (err) { 
      console.error('Save site identity error:', err);
      toast.error('Failed to save site identity'); 
    }
    finally { setIsSaving(false); }
  };

  // Legal content handlers
  const handleSaveTerms = async () => {
    setIsSaving(true);
    try {
      await axios.post(`${API}/admin/cms/legal/terms`, { 
        content: termsContent,
        last_updated: new Date().toISOString()
      }, { headers: getAuthHeaders() });
      toast.success('Terms and Conditions saved!');
    } catch (err) {
      console.error('Save terms error:', err);
      toast.error('Failed to save Terms and Conditions');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSavePrivacy = async () => {
    setIsSaving(true);
    try {
      await axios.post(`${API}/admin/cms/legal/privacy`, { 
        content: privacyContent,
        last_updated: new Date().toISOString()
      }, { headers: getAuthHeaders() });
      toast.success('Privacy Policy saved!');
    } catch (err) {
      console.error('Save privacy error:', err);
      toast.error('Failed to save Privacy Policy');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <AdminSidebar />
      <AdminMobileNav />

      <main className="flex-1 p-4 md:p-8 md:ml-64 pt-16 md:pt-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/admin')}
              className="w-10 h-10 rounded-full hover:bg-stone-200 flex items-center justify-center md:hidden"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-[Poppins]">Content Management</h1>
              <p className="text-slate-500">Easily manage your website content</p>
            </div>
          </div>
        </div>

        {/* Help Card */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-4 mb-6 border border-amber-100 flex flex-wrap items-center justify-between gap-4">
          <p className="text-amber-800 text-sm">
            <strong>💡 Tip:</strong> Changes you make here will appear on your public website. 
            Toggle items on/off to show or hide them without deleting.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowRestoreDialog(true)}
            className="bg-white text-amber-700 border-amber-300 hover:bg-amber-50"
            data-testid="restore-defaults-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Restore Defaults
          </Button>
        </div>

        <Tabs defaultValue="landing" className="w-full">
          <TabsList className="mb-6 flex-wrap h-auto gap-1 bg-white p-1 rounded-xl shadow-sm">
            <TabsTrigger value="landing" className="rounded-lg" data-testid="landing-tab">
              <Image className="w-4 h-4 mr-2" />
              Landing Page
            </TabsTrigger>
            <TabsTrigger value="testimonials" className="rounded-lg" data-testid="testimonials-tab">
              <MessageSquare className="w-4 h-4 mr-2" />
              Reviews ({cmsData.testimonials.length})
            </TabsTrigger>
            <TabsTrigger value="faqs" className="rounded-lg" data-testid="faqs-tab">
              <HelpCircle className="w-4 h-4 mr-2" />
              FAQs ({cmsData.faqs.length})
            </TabsTrigger>
            <TabsTrigger value="legal" className="rounded-lg" data-testid="legal-tab">
              <FileText className="w-4 h-4 mr-2" />
              Legal Pages
            </TabsTrigger>
          </TabsList>
          {/* Landing Page Tab */}
          <TabsContent value="landing">
            <CMSLandingTab
              siteIdentity={siteIdentity}
              setSiteIdentity={setSiteIdentity}
              heroForm={heroForm}
              setHeroForm={setHeroForm}
              footerForm={footerForm}
              setFooterForm={setFooterForm}
              isSaving={isSaving}
              onSaveSiteIdentity={handleSaveSiteIdentity}
              onSaveHero={handleSaveHero}
              onSaveFooter={handleSaveFooter}
            />
          </TabsContent>

          {/* Testimonials Tab */}
          <TabsContent value="testimonials">
            <CMSTestimonialsTab
              testimonials={cmsData.testimonials}
              onAdd={() => {
                setEditItem(null);
                setTestimonialForm({ name: '', location: '', rating: 5, text: '', avatar_url: '', is_active: true });
                setActiveDialog('testimonial');
              }}
              onEdit={(testimonial) => {
                setEditItem(testimonial);
                setTestimonialForm(testimonial);
                setActiveDialog('testimonial');
              }}
              onDelete={handleDeleteTestimonial}
            />
          </TabsContent>

          {/* FAQs Tab */}
          <TabsContent value="faqs">
            <CMSFAQsTab
              faqs={cmsData.faqs}
              onAdd={() => {
                setEditItem(null);
                setFaqForm({ question: '', answer: '', category: 'general', order: cmsData.faqs.length, is_active: true });
                setActiveDialog('faq');
              }}
              onEdit={(faq) => {
                setEditItem(faq);
                setFaqForm(faq);
                setActiveDialog('faq');
              }}
              onDelete={handleDeleteFaq}
            />
          </TabsContent>

          {/* Legal Pages Tab */}
          <TabsContent value="legal">
            <CMSLegalTab
              termsContent={termsContent}
              setTermsContent={setTermsContent}
              termsLastUpdated={termsLastUpdated}
              privacyContent={privacyContent}
              setPrivacyContent={setPrivacyContent}
              privacyLastUpdated={privacyLastUpdated}
              isSaving={isSaving}
              onSaveTerms={handleSaveTerms}
              onSavePrivacy={handleSavePrivacy}
            />
          </TabsContent>
        </Tabs>
      </main>

      {/* Testimonial Dialog */}
      <TestimonialDialog
        open={activeDialog === 'testimonial'}
        onClose={closeDialog}
        editItem={editItem}
        form={testimonialForm}
        setForm={setTestimonialForm}
        onSave={handleSaveTestimonial}
        isSaving={isSaving}
      />

      {/* FAQ Dialog */}
      <FAQDialog
        open={activeDialog === 'faq'}
        onClose={closeDialog}
        editItem={editItem}
        form={faqForm}
        setForm={setFaqForm}
        onSave={handleSaveFaq}
        isSaving={isSaving}
      />

      {/* Restore Defaults Dialog */}
      <RestoreDefaultsDialog
        open={showRestoreDialog}
        onClose={setShowRestoreDialog}
        options={restoreOptions}
        setOptions={setRestoreOptions}
        onRestore={handleRestoreDefaults}
        isRestoring={isRestoring}
      />
    </div>
  );
}
